import React from "react";
import ServicePageShell from "../ServicePageShell/ServicePageShell";
import { useIsDanish } from "../../i18n/LanguageContext";

const AIGovernance = () => {
  const isDa = useIsDanish();
  const governanceServices = [
    {
      title: "Microsoft Purview for AI",
      desc: "Discover, classify, and protect the data your AI touches — with sensitivity labels and DLP that follow the data into every prompt.",
      points: [
        "Auto-classify & label sensitive data",
        "DLP policies for AI (Copilot & custom)",
        "Data lineage & risk visibility",
      ],
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M12 3 20 7v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V7l8-4Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M9 12l2 2 4-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      title: "Entra ID Access Control for AI",
      desc: "Decide exactly who can use which AI tools — with role-based access, Conditional Access, and just-in-time approvals.",
      points: [
        "RBAC for AI apps, agents & endpoints",
        "Conditional Access (device, location, risk)",
        "Block shadow AI & unsanctioned tools",
      ],
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M4 21a8 8 0 0 1 16 0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M18.5 7.5 21 10l-2.5 2.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      title: "Azure AI Content Safety",
      desc: "Guardrails on every model: filter harmful content, catch prompt-injection and jailbreaks, and check answers are grounded in real data.",
      points: [
        "Harmful content & abuse filtering",
        "Prompt injection / jailbreak detection",
        "Groundedness & hallucination checks",
      ],
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M12 3 20 6v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M12 8v4M12 15h.01"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      title: "Azure Policy & Landing Zone for AI",
      desc: "Enforce the rules automatically: approved models only, approved regions, private networking, and logging switched on by default.",
      points: [
        "Allow only approved AI models / regions",
        "Require private endpoints & no public access",
        "Enforce diagnostic logging on AI resources",
      ],
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M7 4h10a2 2 0 0 1 2 2v14H5V6a2 2 0 0 1 2-2Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M8 8h8M8 12h8M8 16h5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      title: "Responsible AI, Logging & Audit",
      desc: "Prove what happened. Every prompt, response, and access event is monitored, retained, and ready for an auditor.",
      points: [
        "Prompt & response logging (Azure Monitor)",
        "Usage monitoring & anomaly alerts",
        "Audit trails for compliance reviews",
      ],
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M4 19V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M7 15l3-3 3 2 4-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M4 19h16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      title: "AI Foundry Guardrails & Model Governance",
      desc: "Govern the models themselves: a curated catalog, evaluations before release, content filters, and version control — as code.",
      points: [
        "Approved model catalog & versioning",
        "Evaluations & safety testing pre-release",
        "Governance as code (Bicep + pipelines)",
      ],
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M7 7 4 12l3 5M17 7l3 5-3 5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14 6 10 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
  ];

  const whyImportant = [
    {
      title: "Shadow AI is already here",
      desc: "Your teams are pasting company data into public AI tools right now — whether it's approved or not.",
    },
    {
      title: "Data leakage is the #1 risk",
      desc: "Without controls, confidential documents, customer data, and secrets can flow straight into models you don't own.",
    },
    {
      title: "Regulation is coming fast",
      desc: "The EU AI Act and industry rules expect you to show how AI is controlled, monitored, and kept safe.",
    },
    {
      title: "Trust requires control",
      desc: "Staff and customers only trust AI when there are clear guardrails, audit trails, and accountability.",
    },
  ];

  const useCases = [
    {
      title: "Stop sensitive data leaking into AI",
      desc: "Purview labels + DLP block confidential data from ever reaching an AI prompt.",
    },
    {
      title: "Block unapproved public AI tools",
      desc: "Conditional Access and policy keep staff on sanctioned, secure AI — not random public sites.",
    },
    {
      title: "Prove compliance to auditors",
      desc: "Full logging and audit trails turn 'trust us' into demonstrable evidence.",
    },
    {
      title: "Detect prompt injection & misuse",
      desc: "Content Safety flags jailbreaks, harmful output, and ungrounded answers before they cause harm.",
    },
  ];

  const conventions = [
    "Approved model catalog only — no unsanctioned models in production",
    "All AI access through Entra ID + Conditional Access",
    "Content filters enabled on every AI deployment",
    "Prompt & response logging retained for audit",
    "Sensitivity labels & DLP enforced via Microsoft Purview",
    "Governance deployed as code (PR + pipeline), never click-ops",
  ];

  const whyImportantDa = [
    {
      title: "Shadow AI er allerede her",
      desc: "Medarbejdere bruger allerede AI-værktøjer. Uden styring kan virksomhedsdata ende steder, I ikke har kontrol over.",
    },
    {
      title: "Datalæk er den største risiko",
      desc: "Fortrolige dokumenter, kundedata og secrets kan ryge direkte ind i modeller eller tjenester, I ikke ejer.",
    },
    {
      title: "Regulering kommer hurtigt",
      desc: "EU AI Act og branchekrav forventer, at I kan vise hvordan AI styres, overvåges og holdes sikkert.",
    },
    {
      title: "Tillid kræver kontrol",
      desc: "Medarbejdere, ledelse og kunder kan først stole på AI, når adgang, data, logging og ansvar er tydeligt defineret.",
    },
  ];

  const useCasesDa = [
    {
      title: "Stop følsomme data i at lække til AI",
      desc: "Purview labels og DLP hjælper med at forhindre, at fortrolige data ender i prompts.",
    },
    {
      title: "Bloker ikke-godkendte offentlige AI-værktøjer",
      desc: "Conditional Access og policies holder brugere på godkendte og sikre AI-løsninger.",
    },
    {
      title: "Dokumentér compliance over for audit",
      desc: "Logging og audit trails gør AI-brug dokumenterbar i stedet for baseret på tillid alene.",
    },
    {
      title: "Opdag prompt injection og misbrug",
      desc: "Content Safety kan flagge jailbreaks, skadeligt output og svar uden grounding, før det bliver et problem.",
    },
  ];

  const conventionsDa = [
    "Kun godkendt modelkatalog, ingen uautoriserede modeller i produktion",
    "Al AI-adgang gennem Entra ID og Conditional Access",
    "Content filters aktiveret på alle AI-deployments",
    "Prompt- og responslogging gemmes til audit",
    "Sensitivity labels og DLP håndhæves via Microsoft Purview",
    "Governance deployes som kode via PR og pipeline, aldrig click-ops",
  ];

  const currentWhyImportant = isDa ? whyImportantDa : whyImportant;
  const currentUseCases = isDa ? useCasesDa : useCases;
  const currentConventions = isDa ? conventionsDa : conventions;

  return (
    <ServicePageShell>
      <div className="gov-wrap">
        {/* HERO */}
        <section className="gov-hero">
          <div className="gov-heroInner">
            <div className="gov-heroText">
              <div className="gov-pill">
                <span className="gov-dot" /> AI Governance
              </div>

              <h1 className="gov-title">
                {isDa ? "Håndhæv AI governance på tværs af organisationen." : "Enforce AI governance across your organisation — through the Microsoft platform."}
              </h1>

              <p className="gov-subtitle">
                {isDa ? "AI er stærkt, men AI uden governance kan lække data, bryde compliance og svække tillid. Vi bruger blandt andet Purview, Entra ID, Content Safety og Azure Policy til at sætte reelle guardrails omkring modeller, prompts og brugere, så I kan bruge AI uden at miste kontrollen." : "AI is powerful, but ungoverned AI leaks data, breaks compliance, and erodes trust. We use Microsoft Purview, Entra ID, Azure AI Content Safety, and Azure Policy to put real guardrails around every model, prompt, and user — so you get AI's upside without the risk."}
              </p>

              <div className="gov-ctaRow">
                <a className="gov-btnPrimary" href="/#contact-section">
                  {isDa ? "Styr jeres AI" : "Govern your AI"} <span aria-hidden="true">→</span>
                </a>
                <a className="gov-btnGhost" href="/use-cases">
                  {isDa ? "Se cases" : "View use cases"}
                </a>
              </div>

              <div className="gov-trust">
                <div className="gov-trustItem">
                  <strong>{isDa ? "Kontrolleret" : "Controlled"}</strong>
                  <span>{isDa ? "Adgang og data" : "Access & data"}</span>
                </div>
                <div className="gov-divider" />
                <div className="gov-trustItem">
                  <strong>{isDa ? "Sikker" : "Safe"}</strong>
                  <span>{isDa ? "Content filtreret" : "Content filtered"}</span>
                </div>
                <div className="gov-divider" />
                <div className="gov-trustItem">
                  <strong>{isDa ? "Auditerbar" : "Auditable"}</strong>
                  <span>{isDa ? "Fuldt logget" : "Fully logged"}</span>
                </div>
              </div>
            </div>

            <div className="gov-heroCard">
              <div className="gov-cardHead">
                <h3>{isDa ? "Hvad AI governance forebygger" : "What AI governance prevents"}</h3>
                <p>{isDa ? "Risici der kan gøre AI fra en styrke til et ansvar." : "The risks that turn AI from an asset into a liability."}</p>
              </div>

              <ul className="gov-bullets">
                <li>
                  <span className="gov-check" aria-hidden="true">
                    ✓
                  </span>
                  {isDa ? "Fortrolige data pasted ind i offentlig AI" : "Confidential data pasted into public AI"}
                </li>
                <li>
                  <span className="gov-check" aria-hidden="true">
                    ✓
                  </span>
                  {isDa ? "Ikke-godkendte modeller og shadow AI" : "Unapproved models & shadow AI"}
                </li>
                <li>
                  <span className="gov-check" aria-hidden="true">
                    ✓
                  </span>
                  {isDa ? "Prompt injection og skadeligt output" : "Prompt injection & harmful output"}
                </li>
                <li>
                  <span className="gov-check" aria-hidden="true">
                    ✓
                  </span>
                  {isDa ? "Ingen sporbarhed på hvem der brugte AI og hvordan" : "No record of who used AI, or how"}
                </li>
              </ul>

              <div className="gov-note">
                <span className="gov-noteBadge">{isDa ? "Virkelighed" : "Reality"}</span>
                <span>
                  {isDa ? "AI governance handler ikke om at bremse teams. Det er det, der gør det muligt at sige ja til AI med tillid." : "AI governance isn't about slowing teams down — it's what lets you say \"yes\" to AI with confidence."}
                </span>
              </div>
            </div>
          </div>

          <div className="gov-glow gov-glow1" />
          <div className="gov-glow gov-glow2" />
        </section>

        {/* WHY IMPORTANT */}
        <section className="gov-section gov-why">
          <div className="gov-sectionHead">
            <h2>{isDa ? "Hvorfor det er vigtigt nu" : "Why it matters now"}</h2>
            <p>
              {isDa ? "AI bliver taget i brug hurtigere end mange organisationer kan sikre det. Governance lukker det hul, før det bliver et problem." : "AI adoption is happening faster than most organisations can secure it — governance closes that gap before it becomes a headline."}
            </p>
          </div>

          <div className="gov-whyGrid">
            {currentWhyImportant.map((w) => (
              <div key={w.title} className="gov-whyCard">
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SERVICES */}
        <section className="gov-section">
          <div className="gov-sectionHead">
            <h2>{isDa ? "Hvordan vi håndhæver det" : "How we enforce it — on Microsoft"}</h2>
            <p>
              {isDa ? "En lagdelt governance-model for data, identitet, safety, policy, audit og modelkontrol." : "A layered governance model built entirely on Microsoft technology: data, identity, safety, policy, audit, and model control."}
            </p>
          </div>

          <div className="gov-grid">
            {governanceServices.map((s) => (
              <div key={s.title} className="gov-card">
                <div className="gov-cardIcon">{s.icon}</div>
                <div className="gov-cardBody">
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                  <ul className="gov-points">
                    {s.points.map((p) => (
                      <li key={p}>
                        <span aria-hidden="true">→</span> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* USE CASES */}
        <section className="gov-section gov-usecases">
          <div className="gov-sectionHead">
            <h2>{isDa ? "Reelle use cases" : "Real use cases"}</h2>
            <p>{isDa ? "Praktiske AI governance-scenarier der beskytter jer fra dag ét." : "Practical AI governance scenarios that protect you from day one."}</p>
          </div>

          <div className="gov-useGrid">
            {currentUseCases.map((u) => (
              <div key={u.title} className="gov-useCard">
                <h3>{u.title}</h3>
                <p>{u.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CONVENTIONS */}
        <section className="gov-section gov-conv">
          <div className="gov-sectionHead">
            <h2>{isDa ? "Guardrails og gode praksisser" : "Guardrails & good practices"}</h2>
            <p>
              {isDa ? "Tydelige regler der kan håndhæves, så AI kan skaleres sikkert i hele organisationen." : "Clear, enforceable rules that make AI safe to scale across the whole organisation."}
            </p>
          </div>

          <div className="gov-convCard">
            <ul className="gov-convList">
              {currentConventions.map((c) => (
                <li key={c}>
                  <span className="gov-check" aria-hidden="true">
                    ✓
                  </span>
                  {c}
                </li>
              ))}
            </ul>

            <div className="gov-banner">
              <div>
                <h3>{isDa ? "Vil I have en AI governance baseline for jeres tenant?" : "Want an AI governance baseline for your tenant?"}</h3>
                <p>
                  {isDa ? "Vi kan implementere data-, identitets-, safety- og policy-kontroller på tværs af Microsoft 365 og Azure, deployet via PR og pipeline, så hver ændring er kontrolleret og auditerbar." : "We can implement data, identity, safety, and policy controls across Microsoft 365 and Azure — deployed via PR + pipeline so every change is controlled and auditable."}
                </p>
              </div>
              <a className="gov-btnPrimary" href="/#contact-section">
                {isDa ? "Byg baseline" : "Build the baseline"} <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="gov-section gov-final">
          <div className="gov-finalCard">
            <h2>{isDa ? "Klar til at bruge AI uden unødig risiko?" : "Ready to adopt AI without the risk?"}</h2>
            <p>
              {isDa ? "Fortæl hvordan AI bruges i organisationen i dag. Vi foreslår en klar governance-model for data, identitet, safety, policy og audit, tilpasset jeres virkelighed." : "Tell us how AI is being used in your organisation today. We'll propose a clear governance model — data, identity, safety, policy, and audit — built on Microsoft and tailored to your reality."}
            </p>
            <div className="gov-ctaRow">
              <a className="gov-btnPrimary" href="/#contact-section">
                {isDa ? "Kontakt os" : "Contact us"} <span aria-hidden="true">→</span>
              </a>
              <a className="gov-btnGhost" href="/use-cases">
                {isDa ? "Se ydelser" : "Explore services"}
              </a>
            </div>
          </div>
        </section>

        {/* CSS */}
        <style>{`
          .gov-wrap{
            max-width: 1100px;
            margin: 0 auto;
            padding: 28px 18px 70px;
            font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, "Apple Color Emoji", "Segoe UI Emoji";
            color: #0f172a;
          }

          /* HERO */
          .gov-hero{
            position: relative;
            border-radius: 28px;
            padding: 34px 20px;
            overflow: hidden;
            background:
              radial-gradient(1200px 500px at 10% 10%, rgba(59,130,246,0.18), transparent 60%),
              radial-gradient(900px 420px at 90% 10%, rgba(168,85,247,0.16), transparent 58%),
              linear-gradient(180deg, #ffffff, #f8fafc);
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow:
              0 20px 60px rgba(2, 6, 23, 0.10),
              0 2px 12px rgba(2, 6, 23, 0.06);
          }

          .gov-heroInner{
            display: grid;
            grid-template-columns: 1.2fr 0.8fr;
            gap: 22px;
            align-items: center;
            position: relative;
            z-index: 2;
          }

          @media (max-width: 900px){
            .gov-heroInner{ grid-template-columns: 1fr; }
          }

          .gov-pill{
            display: inline-flex;
            align-items: center;
            gap: 10px;
            padding: 8px 12px;
            border-radius: 999px;
            background: rgba(2, 6, 23, 0.04);
            border: 1px solid rgba(15,23,42,0.08);
            font-weight: 800;
            font-size: 0.95rem;
          }

          .gov-dot{
            width: 10px; height: 10px;
            border-radius: 999px;
            background: linear-gradient(135deg, #3b82f6, #a855f7);
            box-shadow: 0 0 0 3px rgba(59,130,246,0.14);
          }

          .gov-title{
            margin: 14px 0 10px;
            font-size: clamp(2rem, 2.6vw, 3.1rem);
            line-height: 1.06;
            letter-spacing: -0.02em;
          }

          .gov-subtitle{
            margin: 0 0 18px;
            font-size: 1.08rem;
            line-height: 1.6;
            color: rgba(15, 23, 42, 0.78);
            max-width: 70ch;
          }

          .gov-ctaRow{
            display: flex;
            gap: 12px;
            flex-wrap: wrap;
            margin-bottom: 18px;
          }

          .gov-btnPrimary{
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            padding: 12px 16px;
            border-radius: 14px;
            text-decoration: none;
            font-weight: 900;
            color: white;
            background: linear-gradient(135deg, #2563eb, #7c3aed);
            box-shadow: 0 14px 30px rgba(37, 99, 235, 0.18);
            border: 1px solid rgba(255,255,255,0.22);
            transition: transform .15s ease, box-shadow .15s ease;
          }
          .gov-btnPrimary:hover{
            transform: translateY(-1px);
            box-shadow: 0 18px 40px rgba(37, 99, 235, 0.22);
          }

          .gov-btnGhost{
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 12px 16px;
            border-radius: 14px;
            text-decoration: none;
            font-weight: 900;
            color: #0f172a;
            background: rgba(255,255,255,0.75);
            border: 1px solid rgba(15,23,42,0.10);
            backdrop-filter: blur(8px);
            transition: transform .15s ease, background .15s ease;
          }
          .gov-btnGhost:hover{
            transform: translateY(-1px);
            background: rgba(255,255,255,0.95);
          }

          .gov-trust{
            display: inline-flex;
            align-items: center;
            gap: 14px;
            padding: 12px 14px;
            border-radius: 18px;
            background: rgba(255,255,255,0.70);
            border: 1px solid rgba(15,23,42,0.08);
            backdrop-filter: blur(10px);
          }
          .gov-trustItem{ display: grid; line-height: 1.1; }
          .gov-trustItem span{
            font-size: 0.9rem;
            color: rgba(15,23,42,0.7);
          }
          .gov-divider{
            width: 1px;
            height: 28px;
            background: rgba(15,23,42,0.12);
          }

          .gov-heroCard{
            border-radius: 22px;
            background: rgba(255,255,255,0.70);
            border: 1px solid rgba(15,23,42,0.10);
            box-shadow: 0 18px 50px rgba(2, 6, 23, 0.10);
            padding: 16px;
            backdrop-filter: blur(10px);
          }
          .gov-cardHead h3{
            margin: 0;
            font-size: 1.1rem;
            letter-spacing: -0.01em;
          }
          .gov-cardHead p{
            margin: 6px 0 0;
            color: rgba(15,23,42,0.72);
            line-height: 1.5;
          }

          .gov-bullets{
            margin: 12px 0 0;
            padding: 0;
            list-style: none;
            display: grid;
            gap: 10px;
            color: rgba(15,23,42,0.78);
          }
          .gov-bullets li{
            display: grid;
            grid-template-columns: 24px 1fr;
            gap: 10px;
            align-items: start;
            padding: 12px 12px;
            border-radius: 16px;
            background: rgba(255,255,255,0.75);
            border: 1px solid rgba(15,23,42,0.08);
          }

          .gov-check{
            width: 24px; height: 24px;
            border-radius: 999px;
            display: grid;
            place-items: center;
            background: rgba(37,99,235,0.12);
            border: 1px solid rgba(37,99,235,0.18);
            font-weight: 900;
            line-height: 1;
          }

          .gov-note{
            margin-top: 12px;
            padding: 12px;
            border-radius: 16px;
            background: linear-gradient(135deg, rgba(37,99,235,0.10), rgba(124,58,237,0.08));
            border: 1px solid rgba(15,23,42,0.10);
            display: flex;
            gap: 10px;
            align-items: flex-start;
            color: rgba(15,23,42,0.78);
            line-height: 1.5;
          }
          .gov-noteBadge{
            display: inline-flex;
            align-items: center;
            justify-content: center;
            height: 24px;
            padding: 0 10px;
            border-radius: 999px;
            background: rgba(255,255,255,0.7);
            border: 1px solid rgba(15,23,42,0.08);
            font-weight: 900;
            font-size: 0.85rem;
            white-space: nowrap;
          }

          /* Decorative glows inside hero */
          .gov-glow{
            position: absolute;
            width: 320px;
            height: 320px;
            border-radius: 999px;
            filter: blur(40px);
            opacity: 0.6;
            z-index: 1;
            pointer-events: none;
          }
          .gov-glow1{
            left: -120px;
            top: -120px;
            background: rgba(59,130,246,0.35);
          }
          .gov-glow2{
            right: -120px;
            top: -120px;
            background: rgba(168,85,247,0.30);
          }

          /* SECTIONS */
          .gov-section{ margin-top: 34px; }

          .gov-sectionHead{
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            margin-bottom: 14px;
          }
          .gov-sectionHead h2{
            margin: 0;
            font-size: 1.7rem;
            letter-spacing: -0.02em;
          }
          .gov-sectionHead p{
            margin: 0;
            color: rgba(15,23,42,0.72);
            line-height: 1.55;
          }

          /* WHY grid */
          .gov-whyGrid{
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
          }
          @media (max-width: 760px){
            .gov-whyGrid{ grid-template-columns: 1fr; }
          }
          .gov-whyCard{
            border-radius: 18px;
            padding: 14px;
            background: white;
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow: 0 10px 30px rgba(2,6,23,0.05);
          }
          .gov-whyCard h3{
            margin: 0 0 6px;
            font-size: 1.02rem;
            letter-spacing: -0.01em;
          }
          .gov-whyCard p{
            margin: 0;
            color: rgba(15,23,42,0.72);
            line-height: 1.55;
          }

          /* SERVICES grid */
          .gov-grid{
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 14px;
          }
          @media (max-width: 980px){
            .gov-grid{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
          }
          @media (max-width: 620px){
            .gov-grid{ grid-template-columns: 1fr; }
          }

          .gov-card{
            border-radius: 20px;
            background: white;
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow: 0 10px 30px rgba(2,6,23,0.06);
            padding: 16px;
            display: grid;
            grid-template-columns: 44px 1fr;
            gap: 12px;
            transition: transform .15s ease, box-shadow .15s ease;
            min-height: 150px;
          }
          .gov-card:hover{
            transform: translateY(-2px);
            box-shadow: 0 18px 48px rgba(2,6,23,0.09);
          }

          .gov-cardIcon{
            width: 44px;
            height: 44px;
            border-radius: 14px;
            display: grid;
            place-items: center;
            background: linear-gradient(180deg, rgba(59,130,246,0.12), rgba(168,85,247,0.10));
            border: 1px solid rgba(15,23,42,0.08);
            color: #0f172a;
          }
          .icon{ width: 22px; height: 22px; }

          .gov-cardBody h3{
            margin: 0;
            font-size: 1.05rem;
            letter-spacing: -0.01em;
          }
          .gov-cardBody p{
            margin: 6px 0 10px;
            color: rgba(15,23,42,0.72);
            line-height: 1.55;
            font-size: 0.98rem;
          }

          .gov-points{
            margin: 0;
            padding: 0;
            list-style: none;
            display: grid;
            gap: 8px;
            color: rgba(15,23,42,0.78);
            line-height: 1.45;
            font-size: 0.96rem;
          }
          .gov-points li{
            display: grid;
            grid-template-columns: 18px 1fr;
            gap: 8px;
            align-items: start;
            padding: 10px 12px;
            border-radius: 16px;
            background: rgba(255,255,255,0.75);
            border: 1px solid rgba(15,23,42,0.08);
          }

          /* USE CASES */
          .gov-useGrid{
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
          }
          @media (max-width: 760px){
            .gov-useGrid{ grid-template-columns: 1fr; }
          }
          .gov-useCard{
            border-radius: 18px;
            padding: 14px;
            background: white;
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow: 0 10px 30px rgba(2,6,23,0.05);
          }
          .gov-useCard h3{
            margin: 0 0 6px;
            font-size: 1.02rem;
            letter-spacing: -0.01em;
          }
          .gov-useCard p{
            margin: 0;
            color: rgba(15,23,42,0.72);
            line-height: 1.55;
          }

          /* CONVENTIONS */
          .gov-convCard{
            border-radius: 26px;
            padding: 22px;
            background:
              radial-gradient(900px 360px at 10% 20%, rgba(59,130,246,0.10), transparent 55%),
              radial-gradient(900px 360px at 90% 20%, rgba(168,85,247,0.09), transparent 55%),
              linear-gradient(180deg, #ffffff, #f8fafc);
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow: 0 18px 50px rgba(2,6,23,0.07);
          }

          .gov-convList{
            margin: 0;
            padding: 0;
            list-style: none;
            display: grid;
            gap: 10px;
          }
          .gov-convList li{
            display: grid;
            grid-template-columns: 24px 1fr;
            gap: 10px;
            align-items: start;
            padding: 12px 12px;
            border-radius: 16px;
            background: rgba(255,255,255,0.75);
            border: 1px solid rgba(15,23,42,0.08);
          }

          .gov-banner{
            margin-top: 16px;
            border-radius: 22px;
            padding: 18px;
            background: linear-gradient(135deg, rgba(37,99,235,0.12), rgba(124,58,237,0.10));
            border: 1px solid rgba(15,23,42,0.10);
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 14px;
            flex-wrap: wrap;
          }
          .gov-banner h3{
            margin: 0 0 6px;
            font-size: 1.15rem;
            letter-spacing: -0.01em;
          }
          .gov-banner p{
            margin: 0;
            color: rgba(15,23,42,0.72);
            line-height: 1.5;
            max-width: 78ch;
          }

          /* FINAL CTA */
          .gov-finalCard{
            border-radius: 26px;
            padding: 22px;
            background:
              radial-gradient(900px 360px at 10% 20%, rgba(59,130,246,0.10), transparent 55%),
              radial-gradient(900px 360px at 90% 20%, rgba(168,85,247,0.09), transparent 55%),
              linear-gradient(180deg, #ffffff, #f8fafc);
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow: 0 18px 50px rgba(2,6,23,0.07);
          }
          .gov-finalCard h2{
            margin: 0 0 10px;
            font-size: 1.7rem;
            letter-spacing: -0.02em;
          }
          .gov-finalCard p{
            margin: 0 0 14px;
            color: rgba(15,23,42,0.72);
            line-height: 1.6;
            max-width: 78ch;
          }
        `}</style>
      </div>
    </ServicePageShell>
  );
};

export default AIGovernance;
